import { createContext, useCallback, useMemo } from "react";

import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    useHomePageJPDXflowTargetingCohort,
    useHomePageJSMXflowTargetingCohort,
    useHomePageLoomXflowTargetingCohort,
    useLoggedInHasJiraAndNotConfluence,
    useLoggedOut
} from "@/components/user-context";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import { GlobalFooter } from "@/patterns/global-footer/src";
import GlobalNav from "@/patterns/global-nav/src";
import { FeatureExposed } from "@/utils/feature-exposed";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { ParentScrollTracking } from "@/utils/scroll-tracking";

import StickyContainer from "../../../../patterns/sticky-container/sticky-container";

import {
    FutureOfTeamwork,
    CustomerStories,
    GetStartedCTA,
    HeroJiraCtaTemplate,
    ScrollPinnedSections,
    TabsAndIntegrationsSections,
    HomePageBannerAlert,
    TeamStats,
    ConfluenceCrossflowCTA,
    LoggedInHeroContainer,
    BelowNavBannerAlert,
    TeamworkCollectionHero,
    AtlassianIntelligence
} from "./sections-v2";
import { HeroTakeoverTeam26 } from "./sections-v2/hero-takeover-team-26";
import { HeroTWCHeader } from "./sections-v2/hero-twc-header";
import { HeroTeamworkCollection } from "./sections-v2/hero-twc-tabs";
import { JiraLoggedInHeader } from "./sections-v2/jira-logged-in-header";
import { JPDCrossflowComponent } from "./sections-v2/jpd-xflow-hero/homepage-jpd-xflow";
import { ContentTypesSection } from "./sections-v2/jsm-xflow-hero/content-types-section";
import { JSMCrossflowComponent } from "./sections-v2/jsm-xflow-hero/homepage-jsm-xflow";
import { LoomCrossflowComponent } from "./sections-v2/loom-xflow-hero/homepage-loom-xflow";
import { ProductProvider } from "./sections-v2/reusable-xflow-hero-component/product-context";
import { RovoInnovationsVideo } from "./sections-v2/rovo-innovations-video";

export const HeroSelectedTeamIndexContext = createContext<number | undefined>(
    undefined
);

type CrossflowProduct = "jsm" | "jpd" | "loom" | "confluence";

/**
 * Determines which targeting cohort is enabled and returns the corresponding product.
 * Checks JSM first, then JPD, then Loom.
 */
const useGetEnabledCrossflowProduct = (): CrossflowProduct | null => {
    const isConfEnabled = useLoggedInHasJiraAndNotConfluence();
    const isJsmEnabled = useHomePageJSMXflowTargetingCohort();
    const isJpdEnabled = useHomePageJPDXflowTargetingCohort();
    const isLoomEnabled = useHomePageLoomXflowTargetingCohort();

    return useMemo(() => {
        if (isConfEnabled) return "confluence";
        if (isJsmEnabled) return "jsm";
        if (isJpdEnabled) return "jpd";
        if (isLoomEnabled) return "loom";
        return null;
    }, [isConfEnabled, isJsmEnabled, isJpdEnabled, isLoomEnabled]);
};

/**
 * Default Jira CTA hero shown when user is not enrolled in any crossflow experiments
 */
const DefaultJiraHero = () => (
    <HeroJiraCtaTemplate>
        <JiraLoggedInHeader />
    </HeroJiraCtaTemplate>
);

export const HomePageTemplate = () => {
    const isLoggedIn = !useLoggedOut();
    const enabledCrossflowProduct = useGetEnabledCrossflowProduct();

    const queryParams =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
            : globalThis.location
            ? new URLSearchParams(globalThis.location.search)
            : null;
    const isTeamchangerVariant =
        queryParams?.get("teamchangerReferral") === "true";

    const wacHomepageRovoVideoModuleUpdateGate = checkFeatureFlagGate(
        featureFlags.gwp10147WacHomepageRovoVideoModuleGate
    );

    // Render the appropriate crossflow hero based on the enabled targeting cohort
    const renderCrossflowHero = useCallback(() => {
        switch (enabledCrossflowProduct) {
            case "confluence":
                return (
                    <FeatureExposed
                        experimentKey="gwp-5831-homepage-confluence-xflow"
                        group="Test"
                    >
                        <ProductProvider product="confluence">
                            <ConfluenceCrossflowCTA
                                contentComponent={ContentTypesSection}
                            />
                        </ProductProvider>
                    </FeatureExposed>
                );
            case "jsm":
                return (
                    <ProductProvider product="jsm">
                        <JSMCrossflowComponent
                            contentComponent={ContentTypesSection}
                        />
                    </ProductProvider>
                );
            case "jpd":
                return (
                    <ProductProvider product="jpd">
                        <JPDCrossflowComponent
                            contentComponent={ContentTypesSection}
                        />
                    </ProductProvider>
                );
            case "loom":
                return (
                    <ProductProvider product="loom">
                        <LoomCrossflowComponent
                            contentComponent={ContentTypesSection}
                        />
                    </ProductProvider>
                );
            default:
                return <DefaultJiraHero />;
        }
    }, [enabledCrossflowProduct]);

    return (
        <UFOSegment name="home-page-v2">
            <div>
                <StickyContainer>
                    <HomePageBannerAlert />
                    <GlobalNav productKey={"home"} />
                    <BelowNavBannerAlert />
                </StickyContainer>
                <TypographyStyles>
                    <main id="content">
                        <ParentScrollTracking
                            scrollTrackingEnabled={true}
                            pageId="home-page-v2"
                        >
                            <HeroTakeoverTeam26 />
                            {isTeamchangerVariant ? (
                                <HeroTeamworkCollection>
                                    <HeroTWCHeader />
                                </HeroTeamworkCollection>
                            ) : isLoggedIn ? (
                                <LoggedInHeroContainer>
                                    {renderCrossflowHero()}
                                </LoggedInHeroContainer>
                            ) : (
                                // user not logged-in
                                <DefaultJiraHero />
                            )}
                            {wacHomepageRovoVideoModuleUpdateGate ? (
                                <RovoInnovationsVideo />
                            ) : (
                                <AtlassianIntelligence />
                            )}
                            <TeamworkCollectionHero />
                            <ScrollPinnedSections />
                            <TeamStats />
                            <TabsAndIntegrationsSections />
                            <CustomerStories />
                            <FutureOfTeamwork />
                            <GetStartedCTA />
                        </ParentScrollTracking>
                    </main>
                </TypographyStyles>
                <GlobalFooter />
            </div>
        </UFOSegment>
    );
};
